import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, FileText, ArrowRight, Download, CheckCircle2, Circle, Eraser, CalendarClock } from "lucide-react";
import { EmailedCopies } from "@/components/EmailedCopies";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";
import { DueBadge } from "@/components/DueBadge";

const calEvent = (s) => ({
  id: s.id, date: s.due_date, title: `File ${s.form_number} — ${s.title}`,
  details: "Filing due date (NC Legal Forms). File with the Clerk of Superior Court in your county.",
  url: `${window.location.origin}/forms/${s.form_number}?submission=${s.id}`,
});
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/context/I18nContext";

export default function SavedForms() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const subscribed = user && (["active", "trialing"].includes(user.subscription_status) || user.role === "admin");

  const load = () => api.get("/submissions").then(({ data }) => setSubs(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { if (subscribed) load(); else setLoading(false); }, [subscribed]);

  const remove = async (id) => {
    try { await api.delete(`/submissions/${id}`); setSubs((s) => s.filter((x) => x.id !== id)); toast.success(t("saved.deleted")); }
    catch { toast.error(t("saved.deleteFail")); }
  };

  const download = async (s) => {
    try {
      const res = await api.get(`/submissions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${s.form_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("saved.downloadFail")); }
  };

  const toggleFiled = async (s) => {
    const filed = s.filing_status !== "filed";
    try {
      const { data } = await api.patch(`/submissions/${s.id}/status`, { filed });
      setSubs((list) => list.map((x) => (x.id === s.id ? { ...x, filing_status: data.filing_status, filed_at: data.filed_at } : x)));
      toast.success(filed ? t("saved.markedFiled") : t("saved.markedNotFiled"));
    } catch { toast.error(t("saved.statusFail")); }
  };

  const setDue = async (s, value) => {
    try {
      const { data } = await api.patch(`/submissions/${s.id}/due-date`, { due_date: value || null });
      setSubs((list) => list.map((x) => (x.id === s.id ? { ...x, due_date: data.due_date } : x)));
      toast.success(value ? "Due date saved — we'll remind you 3 days before and on the day" : "Due date cleared");
    } catch { toast.error("Could not save the due date"); }
  };

  const filedCount = subs.filter((s) => s.filing_status === "filed").length;
  const draftCount = subs.length - filedCount;

  const clearDrafts = async () => {
    try {
      const { data } = await api.delete("/submissions", { params: { status: "draft" } });
      setSubs((s) => s.filter((x) => x.filing_status === "filed"));
      toast.success(t("saved.cleared", { count: data.deleted }));
    } catch { toast.error(t("saved.clearFail")); }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24 min-h-[60vh]">
        <p className="overline text-accent mb-2">{t("saved.eyebrow")}</p>
        <h1 className="font-serif text-5xl tracking-tighter mb-8">{t("saved.title")}</h1>

        {subscribed && subs.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-8 text-sm" data-testid="filing-progress">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-black bg-secondary font-medium">
              <CheckCircle2 size={16} className="text-emerald-600" /> {t("saved.filed", { count: filedCount })}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-black font-medium">
              <Circle size={16} className="text-muted-foreground" /> {t("saved.notFiled", { count: draftCount })}
            </span>
            {draftCount > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="clear-drafts-btn" className="rounded-sm gap-2 text-muted-foreground hover:text-accent">
                    <Eraser size={15} /> {t("saved.clearDrafts")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("saved.clearTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("saved.clearDesc", { count: draftCount })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="clear-drafts-cancel">{t("saved.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={clearDrafts} data-testid="clear-drafts-confirm" className="bg-accent text-accent-foreground hover:bg-accent/90">
                      {t("saved.clearConfirm", { count: draftCount })}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}

        {!subscribed ? (
          <div className="grid-card p-12 text-center">
            <p className="text-muted-foreground mb-4">{t("saved.proFeature")}</p>
            <Link to="/pricing"><Button className="rounded-sm bg-accent text-accent-foreground">{t("saved.upgradeToPro")}</Button></Link>
          </div>
        ) : loading ? (
          <p className="text-muted-foreground">{t("saved.loading")}</p>
        ) : subs.length === 0 ? (
          <div className="grid-card p-12 text-center">
            <FileText className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">{t("saved.noSaved")}</p>
            <Link to="/forms"><Button variant="outline" className="rounded-sm">{t("saved.browseForms")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subs.map((s) => (
              <div key={s.id} className="grid-card p-6 flex flex-col" data-testid={`saved-item-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="rounded-sm font-mono text-xs">{s.form_number}</Badge>
                  <div className="flex items-center gap-3">
                    {s.filing_status !== "filed" && s.due_date && <DueBadge date={s.due_date} id={s.id} />}
                    <button onClick={() => remove(s.id)} data-testid={`delete-${s.id}`} className="text-muted-foreground hover:text-accent transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
                <h3 className="font-serif text-lg leading-tight mb-2 flex-1">{s.title}</h3>
                {s.filing_status === "filed" ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-2" data-testid={`status-badge-${s.id}`}>
                    <CheckCircle2 size={14} /> {t("saved.filedBadge")}{s.filed_at ? ` · ${new Date(s.filed_at).toLocaleDateString()}` : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2" data-testid={`status-badge-${s.id}`}>
                    <Circle size={14} /> {t("saved.notFiledBadge")}
                  </span>
                )}
                <p className="text-xs text-muted-foreground mb-4">{t("saved.updatedAt", { date: new Date(s.updated_at).toLocaleString() })}</p>
                {s.filing_status !== "filed" && (
                  <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground mb-3">
                    <span className="inline-flex items-center gap-1.5"><CalendarClock size={14} /> Filing due</span>
                    <input type="date" value={s.due_date || ""} onChange={(e) => setDue(s, e.target.value)}
                      data-testid={`due-date-${s.id}`}
                      className="bg-transparent border border-border rounded-sm px-2 py-1 text-foreground [color-scheme:light] dark:[color-scheme:dark]" />
                  </label>
                )}
                {s.filing_status !== "filed" && s.due_date && (
                  <div className="flex items-center gap-3 text-xs mb-3 -mt-1" data-testid={`add-calendar-${s.id}`}>
                    <span className="text-muted-foreground">Add to calendar:</span>
                    <a href={googleCalendarUrl(calEvent(s))} target="_blank" rel="noreferrer" data-testid={`gcal-${s.id}`}
                      className="text-accent font-semibold hover:underline">Google</a>
                    <button type="button" onClick={() => downloadIcs(calEvent(s))} data-testid={`ics-${s.id}`}
                      className="text-accent font-semibold hover:underline">Apple / Outlook</button>
                  </div>
                )}
                <Button
                  onClick={() => toggleFiled(s)}
                  data-testid={`toggle-filed-${s.id}`}
                  variant={s.filing_status === "filed" ? "outline" : "default"}
                  className={`w-full rounded-sm gap-2 mb-2 ${s.filing_status === "filed" ? "hover:bg-secondary" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  {s.filing_status === "filed" ? (<><Circle size={15} /> {t("saved.markNotFiled")}</>) : (<><CheckCircle2 size={15} /> {t("saved.markFiled")}</>)}
                </Button>
                <div className="flex gap-2">
                  <Link to={`/forms/${s.form_number}?submission=${s.id}`} className="flex-1" data-testid={`open-submission-${s.id}`}>
                    <Button variant="outline" className="w-full rounded-sm gap-2 hover:bg-secondary">{t("saved.open")} <ArrowRight size={14} /></Button>
                  </Link>
                  <Button onClick={() => download(s)} data-testid={`download-${s.id}`} className="rounded-sm gap-2 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors">
                    <Download size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <EmailedCopies />
      </section>
      <Footer />
    </div>
  );
}
